import * as React from 'react';
import { QueryResult } from 'react-apollo';
import { renderQueryResult } from '../renderQueryResult';
import { LineageCountsCacher, LineageCountsResult } from './LineageCountsCacher';

/**
 * Given a raw query result for a filtered lineage counts query,
 * checks for errors and loading state, and renders the given children accordingly.
 * If there are errors, no children are rendered and the error is logged to the console.
 * If the query is still loading, the children are rendered with cached counts objects.
 * If the query is complete, the children are rendered with new counts objects and the cache is populated.
 * @param result The raw query result for a filtered lineage counts query.
 * @param createUnfilteredCounts Creates an unfiltered counts object from the result data.
 * @param createFilteredCounts Creates a filtered counts object from the result data.
 * @param renderChildren The children to render with the new or cached counts objects.
 */
export function renderFilteredLineageCounts<TCounts, TQuery, TQueryVariables>(
  result: QueryResult<TQuery, TQueryVariables>,
  createUnfilteredCounts: (data: TQuery) => TCounts,
  createFilteredCounts: (data: TQuery) => TCounts,
  renderChildren: (result: LineageCountsResult<TCounts>) => React.ReactNode,
): React.ReactNode {
  return renderQueryResult(result, {
    renderLoading: () => (
      <LineageCountsCacher
        countsLoading={true}
      >
        {renderChildren}
      </LineageCountsCacher>
    ),
    renderData: data => (
      <LineageCountsCacher
        countsLoading={false}
        newUnfilteredCounts={createUnfilteredCounts(data)}
        newFilteredCounts={createFilteredCounts(data)}
      >
        {renderChildren}
      </LineageCountsCacher>
    ),
  });
}

/**
 * Given a raw query result for an unfiltered lineage counts query,
 * checks for errors and loading state, and renders the given children accordingly.
 * If there are errors, no children are rendered and the error is logged to the console.
 * If the query is still loading, the children are rendered with a cached unfiltered counts object.
 * If the query is complete, the children are rendered with a new unfiltered counts object and the cache is populated.
 * Because the query is for unfiltered counts, the children are always rendered with a null filtered counts object.
 * @param result The raw query result for an unfiltered lineage counts query.
 * @param createUnfilteredCounts Creates an unfiltered counts object from the result data.
 * @param renderChildren The children to render with the new or cached counts objects.
 */
export function renderUnfilteredLineageCounts<TCounts, TQuery, TQueryVariables>(
  result: QueryResult<TQuery, TQueryVariables>,
  createUnfilteredCounts: (data: TQuery) => TCounts,
  renderChildren: (result: LineageCountsResult<TCounts>) => React.ReactNode,
): React.ReactNode {
  return renderQueryResult(result, {
    renderLoading: () => (
      <LineageCountsCacher
        countsLoading={true}
        newFilteredCounts={null}
      >
        {renderChildren}
      </LineageCountsCacher>
    ),
    renderData: data => (
      <LineageCountsCacher
        countsLoading={false}
        newUnfilteredCounts={createUnfilteredCounts(data)}
        newFilteredCounts={null}
      >
        {renderChildren}
      </LineageCountsCacher>
    ),
  });
}
